import {
  BookOpen,
  CheckCircle2,
  GraduationCap,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ApiError, type StudyPack, type StudyResult } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Prepare for the interview: what to read, and a quiz on it.
 *
 * The reading comes from PRAXA's own library – the same search that answers
 * chat questions – and every quiz question names the passage it was written
 * from, so the answer can always be checked against the text. The answers
 * stay on the server until you send yours (`tools/study.py`).
 */
export function StudyPanel({
  jobId,
  onChanged,
}: {
  jobId: string;
  onChanged?: () => void;
}) {
  const [pack, setPack] = useState<StudyPack | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.applications
      .study(jobId)
      .then((next) => {
        if (!cancelled) {
          setPack(next);
          setLoaded(true);
        }
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        // 404 simply means no pack yet – that is the normal first state.
        if (!(reason instanceof ApiError && reason.status === 404)) {
          setError((reason as Error).message);
        }
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  async function build() {
    setBusy(true);
    setError(null);
    try {
      setPack(await api.applications.buildStudy(jobId));
      onChanged?.();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded)
    return <p className="text-xs text-muted-foreground">Loading study pack…</p>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <GraduationCap className="size-4 text-primary" aria-hidden />
        <span className="text-sm font-medium">Prepare for this job</span>
        <Button
          size="sm"
          variant={pack ? "ghost" : "outline"}
          disabled={busy}
          onClick={() => void build()}
          title="Two model calls: pick the topics, then write the quiz"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : pack ? (
            <RefreshCw className="size-3.5" aria-hidden />
          ) : (
            <BookOpen className="size-3.5" aria-hidden />
          )}
          {busy ? "Preparing…" : pack ? "Make a new pack" : "Make a study pack"}
        </Button>
        {pack?.bestScore !== null && pack?.bestScore !== undefined ? (
          <Badge variant="secondary">
            best {pack.bestScore}/{pack.quiz.length}
          </Badge>
        ) : null}
      </div>
      {!pack && !busy ? (
        <p className="text-xs text-muted-foreground">
          Picks the topics this posting asks for, finds what to read in
          PRAXA&apos;s library and writes a short quiz on it. The daily agent
          also does this by itself for jobs you have applied to.
        </p>
      ) : null}
      {error ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {pack ? (
        <PackView
          key={pack.createdAt}
          jobId={jobId}
          pack={pack}
          onAnswered={onChanged}
        />
      ) : null}
    </div>
  );
}

function PackView({
  jobId,
  pack,
  onAnswered,
}: {
  jobId: string;
  pack: StudyPack;
  onAnswered?: () => void;
}) {
  return (
    <div className="space-y-4">
      {pack.topics.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Topics:</span>
          {pack.topics.map((topic) => (
            <Badge key={topic} variant="outline">
              {topic}
            </Badge>
          ))}
        </div>
      ) : null}

      {pack.note ? (
        <p className="text-sm text-muted-foreground">{pack.note}</p>
      ) : null}

      {pack.reading.length > 0 ? (
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Read
          </p>
          <ol className="space-y-2">
            {pack.reading.map((item) => (
              <li
                key={item.n}
                className="rounded-lg border bg-background/40 p-2.5"
              >
                <p className="text-sm">
                  <span className="mr-1.5 font-mono text-xs text-muted-foreground">
                    [{item.n}]
                  </span>
                  <span className="font-medium">{item.citation}</span>
                </p>
                {item.summary ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {item.summary}
                  </p>
                ) : null}
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-primary">
                    Read the passage
                  </summary>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed">
                    {item.excerpt}
                  </p>
                </details>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {pack.quiz.length > 0 ? (
        <Quiz jobId={jobId} pack={pack} onAnswered={onAnswered} />
      ) : null}
    </div>
  );
}

function Quiz({
  jobId,
  pack,
  onAnswered,
}: {
  jobId: string;
  pack: StudyPack;
  onAnswered?: () => void;
}) {
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    pack.quiz.map(() => null),
  );
  const [result, setResult] = useState<StudyResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.applications.answerStudy(jobId, answers));
      onAnswered?.();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function retry() {
    setAnswers(pack.quiz.map(() => null));
    setResult(null);
  }

  const answered = answers.filter((answer) => answer !== null).length;

  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Quiz – {pack.quiz.length} question{pack.quiz.length === 1 ? "" : "s"}
      </p>
      <ol className="space-y-3">
        {pack.quiz.map((question, index) => {
          const outcome = result?.results[index];
          return (
            <li
              key={question.question}
              className="rounded-lg border bg-background/40 p-3"
            >
              <fieldset disabled={result !== null}>
                <legend className="text-sm font-medium">
                  {index + 1}. {question.question}
                  <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                    from [{question.source}]
                  </span>
                </legend>
                <div className="mt-2 space-y-1">
                  {question.options.map((option, choice) => {
                    const picked = answers[index] === choice;
                    const right = outcome && outcome.answer === choice;
                    const wrongPick = outcome && picked && !outcome.correct;
                    return (
                      <label
                        key={option}
                        className={cn(
                          "flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm",
                          right && "bg-success/15",
                          wrongPick && "bg-destructive/10",
                        )}
                      >
                        <input
                          type="radio"
                          className="mt-1"
                          name={`q-${jobId}-${index}`}
                          checked={picked}
                          onChange={() =>
                            setAnswers((current) =>
                              current.map((value, i) =>
                                i === index ? choice : value,
                              ),
                            )
                          }
                        />
                        {option}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              {outcome ? (
                <p
                  className={cn(
                    "mt-2 flex items-start gap-1.5 text-xs",
                    outcome.correct ? "text-success" : "text-destructive",
                  )}
                >
                  {outcome.correct ? (
                    <CheckCircle2
                      className="mt-px size-3.5 shrink-0"
                      aria-hidden
                    />
                  ) : (
                    <XCircle className="mt-px size-3.5 shrink-0" aria-hidden />
                  )}
                  <span>
                    {outcome.correct ? "Right. " : "Not quite. "}
                    <span className="text-foreground">
                      {outcome.explanation}
                    </span>
                  </span>
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {result ? (
          <>
            <span className="text-sm font-medium">
              {result.score} of {result.total} right
            </span>
            <span className="text-xs text-muted-foreground">
              best {result.bestScore}/{result.total} · {result.attempts} attempt
              {result.attempts === 1 ? "" : "s"}
            </span>
            <Button size="sm" variant="outline" onClick={retry}>
              Try again
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={busy || answered === 0}
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            Check my answers ({answered}/{pack.quiz.length})
          </Button>
        )}
        {error ? (
          <span className="text-xs text-destructive">{error}</span>
        ) : null}
      </div>
    </div>
  );
}
